import React, { useState, useMemo, useEffect } from 'react';
import {
  IonPage,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonContent,
  IonButtons,
  IonMenuButton,
  IonIcon,
  IonSkeletonText,
  IonRefresher,
  IonRefresherContent,
  IonFab,
  IonFabButton,
  IonAlert,
  useIonToast,
} from '@ionic/react';
import {
  add,
  chevronBack,
  chevronForward,
  documentText,
  alertCircle,
  create,
  trash,
  handLeft,
} from 'ionicons/icons';
import { useLocation } from 'react-router-dom';
import { useVisits, useDeleteVisit } from '@/hooks/useVisits';
import { useNeeds } from '@/hooks/useNeeds';
import { usePermission } from '@/hooks/usePermission';
import type { VisitType, VisitFilters, Visit } from '@/types/visit';
import type { Need, NeedStatus, NeedCategory } from '@/types/need';
import {
  VISIT_TYPE_LABELS,
  NEED_STATUS_LABELS, NEED_STATUS_COLORS,
  NEED_CATEGORY_LABELS, NEED_CATEGORY_COLORS,
  NEED_PRIORITY_LABELS, NEED_PRIORITY_COLORS,
} from '@/utils/labels';
import VisitModal from '@/components/visits/VisitModal';
import NeedModal from '@/components/visits/NeedModal';
import NeedDetailModal from '@/components/visits/NeedDetailModal';

const VISIT_TYPE_COLORS: Record<VisitType, string> = {
  pastorala: '#4f46e5',
  sectoriala: '#0891b2',
  nevoie_speciala: '#dc2626',
  incurajare: '#059669',
  ajutoare: '#d97706',
};

const VisitListPage: React.FC = () => {
  const [presentToast] = useIonToast();
  const location = useLocation();
  const { can } = usePermission();
  const canEditVisits = can('visits.edit');
  const canDeleteVisits = can('visits.delete');

  /* ── URL params (from sector detail links) ── */
  const urlParams = new URLSearchParams(location.search);
  const urlSectorId = urlParams.get('sector_id') || undefined;
  const urlFromDate = urlParams.get('from_date') || undefined;

  /* ── Top-level tabs ── */
  const [mainTab, setMainTab] = useState<'vizite' | 'nevoi'>('vizite');

  /* ── Visit state ── */
  const [activeType, setActiveType] = useState<VisitType | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 15;

  const filters: VisitFilters = {
    sector_id: urlSectorId,
    from_date: urlFromDate,
    visit_type: activeType ?? undefined,
    page: currentPage,
    per_page: pageSize,
  };

  const { data, isLoading, refetch } = useVisits(filters);
  const deleteVisitMut = useDeleteVisit();
  const visits = data?.data ?? [];
  const totalPages = data?.last_page ?? 1;

  /* ── Need state ── */
  const [needStatusFilter, setNeedStatusFilter] = useState<NeedStatus | null>(null);
  const [needCategoryFilter, setNeedCategoryFilter] = useState<NeedCategory | null>(null);
  const [needPage, setNeedPage] = useState(1);
  const { data: needsData, isLoading: needsLoading, refetch: refetchNeeds } = useNeeds({
    status: needStatusFilter ?? undefined,
    category: needCategoryFilter ?? undefined,
    page: needPage,
    per_page: pageSize,
  });
  const needs = needsData?.data ?? [];
  const needTotalPages = needsData?.last_page ?? 1;

  /* ── Modals ── */
  const [showVisitModal, setShowVisitModal] = useState(false);
  const [editingVisit, setEditingVisit] = useState<Visit | null>(null);
  const [deleteVisitId, setDeleteVisitId] = useState<string | null>(null);

  const [showNeedModal, setShowNeedModal] = useState(false);
  const [editingNeed, setEditingNeed] = useState<Need | null>(null);
  const [detailNeedId, setDetailNeedId] = useState<string | null>(null);

  /* ── Mobile detection ── */
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' ? window.innerWidth < 992 : false,
  );

  useEffect(() => {
    const mql = window.matchMedia('(max-width: 991px)');
    const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    mql.addEventListener('change', handler);
    setIsMobile(mql.matches);
    return () => mql.removeEventListener('change', handler);
  }, []);

  const handleRefresh = async (event: CustomEvent) => {
    if (mainTab === 'vizite') await refetch();
    else await refetchNeeds();
    event.detail.complete();
  };

  const handleDeleteVisit = async () => {
    if (!deleteVisitId) return;
    try {
      await deleteVisitMut.mutateAsync(deleteVisitId);
      void presentToast({ message: 'Vizita a fost ștearsă.', duration: 2000, color: 'success', position: 'bottom' });
    } catch {
      void presentToast({ message: 'Eroare la ștergere.', duration: 2000, color: 'danger', position: 'bottom' });
    }
    setDeleteVisitId(null);
  };

  const formatDate = (dateStr: string) => {
    try {
      return new Date(dateStr).toLocaleDateString('ro-RO', { day: '2-digit', month: '2-digit', year: 'numeric' });
    } catch {
      return dateStr;
    }
  };

  /* ── Pagination page numbers ── */
  const makePageNumbers = (total: number, current: number) => {
    const pages: (number | 'ellipsis')[] = [];
    if (total <= 7) {
      for (let i = 1; i <= total; i++) pages.push(i);
    } else {
      pages.push(1);
      if (current > 3) pages.push('ellipsis');
      const start = Math.max(2, current - 1);
      const end = Math.min(total - 1, current + 1);
      for (let i = start; i <= end; i++) pages.push(i);
      if (current < total - 2) pages.push('ellipsis');
      pages.push(total);
    }
    return pages;
  };

  const pageNumbers = useMemo(() => makePageNumbers(totalPages, currentPage), [totalPages, currentPage]);
  const needPageNumbers = useMemo(() => makePageNumbers(needTotalPages, needPage), [needTotalPages, needPage]);

  /* ── Render pagination ── */
  const renderPagination = (
    pages: (number | 'ellipsis')[],
    current: number,
    total: number,
    onPageChange: (p: number) => void,
    mobile?: boolean,
  ) => {
    if (total <= 1) return null;
    if (mobile) {
      return (
        <div className="crm-pagination" style={{ paddingBottom: 80 }}>
          <button className="crm-pagination__btn" disabled={current === 1} onClick={() => onPageChange(current - 1)}>
            <IonIcon icon={chevronBack} style={{ fontSize: 12 }} />
          </button>
          <span style={{ fontSize: 13, fontWeight: 600, color: '#475569', padding: '0 8px' }}>
            {current} / {total}
          </span>
          <button className="crm-pagination__btn" disabled={current === total} onClick={() => onPageChange(current + 1)}>
            <IonIcon icon={chevronForward} style={{ fontSize: 12 }} />
          </button>
        </div>
      );
    }
    return (
      <div className="crm-pagination">
        <button className="crm-pagination__btn" disabled={current === 1} onClick={() => onPageChange(current - 1)}>
          <IonIcon icon={chevronBack} style={{ fontSize: 12 }} /> PREV
        </button>
        {pages.map((p, idx) =>
          p === 'ellipsis' ? (
            <span key={`ellipsis-${idx}`} style={{ padding: '0 4px', color: '#94a3b8' }}>...</span>
          ) : (
            <button
              key={p}
              className={`crm-pagination__num ${p === current ? 'crm-pagination__num--active' : ''}`}
              onClick={() => onPageChange(p)}
            >
              {p}
            </button>
          ),
        )}
        <button className="crm-pagination__btn" disabled={current === total} onClick={() => onPageChange(current + 1)}>
          NEXT <IonIcon icon={chevronForward} style={{ fontSize: 12 }} />
        </button>
      </div>
    );
  };

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar className="crm-toolbar">
          <IonButtons slot="start">
            <IonMenuButton />
          </IonButtons>
          <IonTitle>Vizite & Nevoi</IonTitle>
        </IonToolbar>
      </IonHeader>

      <IonContent>
        <IonRefresher slot="fixed" onIonRefresh={handleRefresh}>
          <IonRefresherContent />
        </IonRefresher>

        <div className="crm-page-layout">
          <div className="crm-main-content">

            {/* ── Main tab selector ── */}
            <div style={{ display: 'flex', gap: 6, marginBottom: 16 }}>
              <button
                className={`form-chip${mainTab === 'vizite' ? ' form-chip--active' : ''}`}
                onClick={() => setMainTab('vizite')}
                style={{ fontSize: 14, padding: '6px 16px' }}
              >
                Vizite
              </button>
              <button
                className={`form-chip${mainTab === 'nevoi' ? ' form-chip--active' : ''}`}
                onClick={() => setMainTab('nevoi')}
                style={{ fontSize: 14, padding: '6px 16px' }}
              >
                Nevoi
              </button>
            </div>

            {/* ═══════════ VISITS TAB ═══════════ */}
            {mainTab === 'vizite' && (
              <>
                {/* Filter chips + bulk button */}
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 16, alignItems: 'center' }}>
                  <button
                    className={`form-chip${!activeType ? ' form-chip--active' : ''}`}
                    onClick={() => { setActiveType(null); setCurrentPage(1); }}
                  >
                    Toate
                  </button>
                  {(Object.keys(VISIT_TYPE_LABELS) as VisitType[]).map((type) => (
                    <button
                      key={type}
                      className={`form-chip${activeType === type ? ' form-chip--active' : ''}`}
                      onClick={() => { setActiveType(activeType === type ? null : type); setCurrentPage(1); }}
                    >
                      {VISIT_TYPE_LABELS[type]}
                    </button>
                  ))}
                </div>

                {/* Loading */}
                {isLoading && (
                  <div>
                    {[1, 2, 3, 4, 5].map((i) => (
                      <div key={i} style={{ padding: '14px 16px', borderBottom: '1px solid #f1f5f9' }}>
                        <IonSkeletonText animated style={{ width: '50%', height: 16 }} />
                        <IonSkeletonText animated style={{ width: '30%', height: 12, marginTop: 6 }} />
                      </div>
                    ))}
                  </div>
                )}

                {/* Desktop: table */}
                {!isLoading && !isMobile && (
                  <div className="crm-card" style={{ overflow: 'auto' }}>
                    <table className="crm-table">
                      <thead>
                        <tr>
                          <th style={{ minWidth: 160 }}>Familie</th>
                          <th style={{ minWidth: 140 }}>Vizitator</th>
                          <th style={{ width: 110 }}>Data</th>
                          <th style={{ width: 130 }}>Tip</th>
                          <th style={{ minWidth: 160 }}>Notițe</th>
                          <th style={{ width: 130 }}>Nevoie Specială</th>
                          {(canEditVisits || canDeleteVisits) && <th style={{ width: 70 }}>Acțiuni</th>}
                        </tr>
                      </thead>
                      <tbody>
                        {visits.map((visit) => {
                          const typeColor = VISIT_TYPE_COLORS[visit.visit_type] ?? '#64748b';
                          return (
                            <tr key={visit.id} className="crm-animate-in">
                              <td>
                                <div className="crm-table__name">{visit.family?.display_name ?? visit.family?.name ?? '—'}</div>
                                {visit.family?.address && <div className="crm-table__sub">{visit.family.address}</div>}
                              </td>
                              <td><span style={{ fontSize: 13, color: '#475569' }}>{visit.visitor?.name ?? '—'}</span></td>
                              <td><span style={{ fontSize: 13, color: '#475569' }}>{formatDate(visit.visit_date)}</span></td>
                              <td>
                                <span className="crm-badge" style={{ backgroundColor: `${typeColor}12`, color: typeColor }}>
                                  {VISIT_TYPE_LABELS[visit.visit_type] ?? visit.visit_type}
                                </span>
                              </td>
                              <td><span style={{ fontSize: 13, color: '#64748b' }}>{visit.notes || '—'}</span></td>
                              <td>
                                {visit.has_special_need ? (
                                  <div>
                                    <IonIcon icon={alertCircle} style={{ fontSize: 14, color: '#dc2626', marginRight: 4, verticalAlign: 'middle' }} />
                                    <span style={{ fontSize: 12, color: '#dc2626' }}>{visit.special_need_type || 'Da'}</span>
                                  </div>
                                ) : (
                                  <span style={{ fontSize: 12, color: '#cbd5e1' }}>—</span>
                                )}
                              </td>
                              {(canEditVisits || canDeleteVisits) && (
                                <td>
                                  <div style={{ display: 'flex', gap: 4 }}>
                                    {canEditVisits && (
                                      <button onClick={() => { setEditingVisit(visit); setShowVisitModal(true); }} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}>
                                        <IonIcon icon={create} style={{ fontSize: 16, color: '#5e81f4' }} />
                                      </button>
                                    )}
                                    {canDeleteVisits && (
                                      <button onClick={() => setDeleteVisitId(visit.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}>
                                        <IonIcon icon={trash} style={{ fontSize: 16, color: '#dc2626' }} />
                                      </button>
                                    )}
                                  </div>
                                </td>
                              )}
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>

                    {visits.length === 0 && (
                      <div className="crm-empty-state">
                        <div className="crm-empty-state__icon"><IonIcon icon={documentText} /></div>
                        <h3 className="crm-empty-state__title">Nicio vizită găsită</h3>
                        <p className="crm-empty-state__text">
                          {activeType ? 'Nu există vizite cu acest tip.' : 'Nu există vizite înregistrate.'}
                        </p>
                      </div>
                    )}

                    {renderPagination(pageNumbers, currentPage, totalPages, setCurrentPage)}
                  </div>
                )}

                {/* Mobile: cards */}
                {!isLoading && isMobile && (
                  <>
                    {visits.map((visit) => {
                      const typeColor = VISIT_TYPE_COLORS[visit.visit_type] ?? '#64748b';
                      return (
                        <div key={visit.id} className="crm-card crm-animate-in" style={{ padding: 16, marginBottom: 10 }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                            <div>
                              <div style={{ fontWeight: 700, fontSize: 15, color: '#1c1d21' }}>{visit.family?.display_name ?? visit.family?.name ?? '—'}</div>
                              {visit.family?.address && <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 2 }}>{visit.family.address}</div>}
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                              <span className="crm-badge" style={{ backgroundColor: `${typeColor}12`, color: typeColor, flexShrink: 0 }}>
                                {VISIT_TYPE_LABELS[visit.visit_type] ?? visit.visit_type}
                              </span>
                              {canEditVisits && (
                                <button onClick={() => { setEditingVisit(visit); setShowVisitModal(true); }} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2 }}>
                                  <IonIcon icon={create} style={{ fontSize: 14, color: '#94a3b8' }} />
                                </button>
                              )}
                              {canDeleteVisits && (
                                <button onClick={() => setDeleteVisitId(visit.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2 }}>
                                  <IonIcon icon={trash} style={{ fontSize: 14, color: '#dc2626' }} />
                                </button>
                              )}
                            </div>
                          </div>
                          <div style={{ display: 'flex', gap: 16, fontSize: 13, color: '#64748b', marginBottom: 6 }}>
                            <span>{formatDate(visit.visit_date)}</span>
                            <span>{visit.visitor?.name ?? '—'}</span>
                          </div>
                          {visit.notes && <p style={{ fontSize: 13, color: '#475569', margin: '4px 0 0' }}>{visit.notes}</p>}
                          {visit.has_special_need && (
                            <div style={{ marginTop: 6, display: 'flex', alignItems: 'center', gap: 4 }}>
                              <IonIcon icon={alertCircle} style={{ fontSize: 14, color: '#dc2626' }} />
                              <span style={{ fontSize: 12, color: '#dc2626' }}>{visit.special_need_type || 'Nevoie specială'}</span>
                            </div>
                          )}
                        </div>
                      );
                    })}

                    {visits.length === 0 && (
                      <div className="crm-empty-state">
                        <div className="crm-empty-state__icon"><IonIcon icon={documentText} /></div>
                        <h3 className="crm-empty-state__title">Nicio vizită găsită</h3>
                        <p className="crm-empty-state__text">
                          {activeType ? 'Nu există vizite cu acest tip.' : 'Nu există vizite înregistrate.'}
                        </p>
                      </div>
                    )}

                    {renderPagination(needPageNumbers, currentPage, totalPages, setCurrentPage, true)}
                  </>
                )}
              </>
            )}

            {/* ═══════════ NEEDS TAB ═══════════ */}
            {mainTab === 'nevoi' && (
              <>
                {/* Status filter chips */}
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 12 }}>
                  <button
                    className={`form-chip${!needStatusFilter ? ' form-chip--active' : ''}`}
                    onClick={() => { setNeedStatusFilter(null); setNeedPage(1); }}
                  >
                    Toate
                  </button>
                  {(Object.keys(NEED_STATUS_LABELS) as NeedStatus[]).map((status) => (
                    <button
                      key={status}
                      className={`form-chip${needStatusFilter === status ? ' form-chip--active' : ''}`}
                      onClick={() => { setNeedStatusFilter(needStatusFilter === status ? null : status); setNeedPage(1); }}
                    >
                      {NEED_STATUS_LABELS[status]}
                    </button>
                  ))}
                </div>

                {/* Category filter chips */}
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 16 }}>
                  {(Object.keys(NEED_CATEGORY_LABELS) as NeedCategory[]).map((cat) => (
                    <button
                      key={cat}
                      className={`form-chip${needCategoryFilter === cat ? ' form-chip--active' : ''}`}
                      onClick={() => { setNeedCategoryFilter(needCategoryFilter === cat ? null : cat); setNeedPage(1); }}
                      style={{ fontSize: 12 }}
                    >
                      {NEED_CATEGORY_LABELS[cat]}
                    </button>
                  ))}
                </div>

                {/* Loading */}
                {needsLoading && (
                  <div>
                    {[1, 2, 3, 4, 5].map((i) => (
                      <div key={i} style={{ padding: '14px 16px', borderBottom: '1px solid #f1f5f9' }}>
                        <IonSkeletonText animated style={{ width: '60%', height: 16 }} />
                        <IonSkeletonText animated style={{ width: '40%', height: 12, marginTop: 6 }} />
                      </div>
                    ))}
                  </div>
                )}

                {/* Desktop: table */}
                {!needsLoading && !isMobile && (
                  <div className="crm-card" style={{ overflow: 'auto' }}>
                    <table className="crm-table">
                      <thead>
                        <tr>
                          <th style={{ minWidth: 180 }}>Titlu</th>
                          <th style={{ minWidth: 140 }}>Familie / Persoană</th>
                          <th style={{ width: 110 }}>Categorie</th>
                          <th style={{ width: 100 }}>Prioritate</th>
                          <th style={{ width: 100 }}>Status</th>
                          <th style={{ width: 100 }}>Data</th>
                        </tr>
                      </thead>
                      <tbody>
                        {needs.map((need) => {
                          const catColor = NEED_CATEGORY_COLORS[need.category] ?? '#64748b';
                          const statusColor = NEED_STATUS_COLORS[need.status] ?? '#64748b';
                          const prioColor = NEED_PRIORITY_COLORS[need.priority] ?? '#475569';
                          return (
                            <tr
                              key={need.id}
                              className="crm-animate-in"
                              onClick={() => setDetailNeedId(need.id)}
                              style={{ cursor: 'pointer' }}
                            >
                              <td>
                                <div className="crm-table__name">{need.title}</div>
                                {need.description && (
                                  <div className="crm-table__sub" style={{ maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    {need.description}
                                  </div>
                                )}
                              </td>
                              <td>
                                <span style={{ fontSize: 13, color: '#475569' }}>
                                  {need.family?.display_name ?? need.family?.name ?? '—'}
                                  {need.member && ` / ${need.member.first_name} ${need.member.last_name}`}
                                </span>
                              </td>
                              <td>
                                <span className="crm-badge" style={{ backgroundColor: `${catColor}12`, color: catColor }}>
                                  {NEED_CATEGORY_LABELS[need.category]}
                                </span>
                              </td>
                              <td>
                                <span style={{ fontSize: 12, fontWeight: 600, color: prioColor }}>
                                  {NEED_PRIORITY_LABELS[need.priority]}
                                </span>
                              </td>
                              <td>
                                <span className="crm-badge" style={{ backgroundColor: `${statusColor}12`, color: statusColor }}>
                                  {NEED_STATUS_LABELS[need.status]}
                                </span>
                              </td>
                              <td><span style={{ fontSize: 13, color: '#475569' }}>{formatDate(need.created_at)}</span></td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>

                    {needs.length === 0 && (
                      <div className="crm-empty-state">
                        <div className="crm-empty-state__icon"><IonIcon icon={handLeft} /></div>
                        <h3 className="crm-empty-state__title">Nicio nevoie găsită</h3>
                        <p className="crm-empty-state__text">
                          {needStatusFilter || needCategoryFilter ? 'Nu există nevoi cu aceste filtre.' : 'Nu există nevoi înregistrate.'}
                        </p>
                      </div>
                    )}

                    {renderPagination(needPageNumbers, needPage, needTotalPages, setNeedPage)}
                  </div>
                )}

                {/* Mobile: cards */}
                {!needsLoading && isMobile && (
                  <>
                    {needs.map((need) => {
                      const catColor = NEED_CATEGORY_COLORS[need.category] ?? '#64748b';
                      const statusColor = NEED_STATUS_COLORS[need.status] ?? '#64748b';
                      return (
                        <div
                          key={need.id}
                          className="crm-card crm-animate-in"
                          style={{ padding: 16, marginBottom: 10, cursor: 'pointer' }}
                          onClick={() => setDetailNeedId(need.id)}
                        >
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 6 }}>
                            <div style={{ fontWeight: 700, fontSize: 15, color: '#1c1d21', flex: 1 }}>{need.title}</div>
                            <span className="crm-badge" style={{ backgroundColor: `${statusColor}12`, color: statusColor, flexShrink: 0 }}>
                              {NEED_STATUS_LABELS[need.status]}
                            </span>
                          </div>
                          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 6 }}>
                            <span className="crm-badge" style={{ backgroundColor: `${catColor}12`, color: catColor }}>
                              {NEED_CATEGORY_LABELS[need.category]}
                            </span>
                            {need.priority !== 'normala' && (
                              <span style={{ fontSize: 11, fontWeight: 600, color: NEED_PRIORITY_COLORS[need.priority] }}>
                                {NEED_PRIORITY_LABELS[need.priority]}
                              </span>
                            )}
                          </div>
                          <div style={{ fontSize: 13, color: '#64748b' }}>
                            {need.family?.display_name ?? need.family?.name ?? '—'}
                            {need.member && ` / ${need.member.first_name} ${need.member.last_name}`}
                            <span style={{ float: 'right', fontSize: 12, color: '#94a3b8' }}>{formatDate(need.created_at)}</span>
                          </div>
                          {need.description && (
                            <p style={{ fontSize: 12, color: '#8181a5', margin: '4px 0 0', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {need.description}
                            </p>
                          )}
                        </div>
                      );
                    })}

                    {needs.length === 0 && (
                      <div className="crm-empty-state">
                        <div className="crm-empty-state__icon"><IonIcon icon={handLeft} /></div>
                        <h3 className="crm-empty-state__title">Nicio nevoie găsită</h3>
                        <p className="crm-empty-state__text">
                          {needStatusFilter || needCategoryFilter ? 'Nu există nevoi cu aceste filtre.' : 'Nu există nevoi înregistrate.'}
                        </p>
                      </div>
                    )}

                    {renderPagination(needPageNumbers, needPage, needTotalPages, setNeedPage, true)}
                  </>
                )}
              </>
            )}
          </div>
        </div>

        {/* ── FAB: Add ── */}
        <IonFab vertical="bottom" horizontal="end" slot="fixed">
          <IonFabButton onClick={() => {
            if (mainTab === 'vizite') {
              setEditingVisit(null);
              setShowVisitModal(true);
            } else {
              setEditingNeed(null);
              setShowNeedModal(true);
            }
          }}>
            <IonIcon icon={add} />
          </IonFabButton>
        </IonFab>

        {/* ── Visit Modal ── */}
        <VisitModal
          isOpen={showVisitModal}
          onClose={() => { setShowVisitModal(false); setEditingVisit(null); }}
          visit={editingVisit}
        />

        {/* ── Need Modal ── */}
        <NeedModal
          isOpen={showNeedModal}
          onClose={() => { setShowNeedModal(false); setEditingNeed(null); }}
          need={editingNeed}
        />

        {/* ── Need Detail ── */}
        <NeedDetailModal
          isOpen={!!detailNeedId}
          onClose={() => setDetailNeedId(null)}
          needId={detailNeedId}
          onEdit={(need) => { setEditingNeed(need); setShowNeedModal(true); }}
        />

        {/* ── Delete Visit Alert ── */}
        <IonAlert
          isOpen={!!deleteVisitId}
          onDidDismiss={() => setDeleteVisitId(null)}
          header="Confirmare Ștergere"
          message="Ești sigur că vrei să ștergi această vizită?"
          buttons={[
            { text: 'Anulează', role: 'cancel' },
            { text: 'Șterge', role: 'destructive', handler: handleDeleteVisit },
          ]}
        />

      </IonContent>
    </IonPage>
  );
};

export default VisitListPage;
